<template>
  <div class="flex gap-3 w-full" :class="message.role === 'user' ? 'justify-end' : ''">
    <!-- Avatar for assistant -->
    <div v-if="message.role === 'assistant'" class="flex-shrink-0">
      <div class="w-8 h-8 rounded-full flex items-center justify-center p-1">
        <img :src="aiLogo" alt="AI" class="w-full h-full" />
      </div>
    </div>

    <!-- Message content -->
    <div class="flex-shrink min-w-0" :style="{ maxWidth: '85%' }">
      <div
        class="rounded-2xl px-4 py-3 break-words overflow-wrap-anywhere message-content"
        :class="message.role === 'user'
          ? 'bg-gray-900 text-white'
          : 'bg-gray-100 text-gray-900'"
      >
        <!-- User messages: plain text -->
        <div v-if="message.role === 'user'" class="text-[15px] leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere">
          {{ message.content }}
        </div>
        <!-- Assistant messages: rendered markdown with code and math -->
        <div v-else class="space-y-3">
          <div v-if="message.reasoning" class="space-y-2">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
              @click="isReasoningVisible = !isReasoningVisible"
            >
              {{ isReasoningVisible ? 'Скрыть размышление' : 'Показать размышление' }}
            </button>
            <div
              v-if="isReasoningVisible"
              class="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 whitespace-pre-wrap break-words overflow-wrap-anywhere"
            >
              {{ message.reasoning }}
            </div>
          </div>
          <div class="text-[15px] leading-relaxed markdown-content" v-html="renderedContent"></div>
        </div>
      </div>
      <!-- Timestamp -->
      <div
        class="text-xs text-gray-500 mt-1 px-1"
        :class="message.role === 'user' ? 'text-right' : ''"
      >
        {{ formatTime(message.timestamp) }}
      </div>
    </div>

    <!-- Avatar for user -->
    <div v-if="message.role === 'user'" class="flex-shrink-0">
      <div class="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center">
        <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed, ref } from 'vue'
import { renderMarkdown } from '../../lib/markdownRenderer.js'
import aiLogo from '/assets/ai_academy_logo.svg'

const props = defineProps({
  message: {
    type: Object,
    required: true
  }
})

const isReasoningVisible = ref(false)

const renderedContent = computed(() => {
  if (props.message.role === 'assistant') {
    return renderMarkdown(props.message.content)
  }
  return props.message.content
})

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
/* Message styling to match ChatGPT */
.overflow-wrap-anywhere {
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
}

/* Markdown content styling */
.markdown-content :deep(p) {
  margin-bottom: 0.5em;
}

.markdown-content :deep(p:last-child) {
  margin-bottom: 0;
}

.markdown-content :deep(pre) {
  background: #282c34;
  border-radius: 8px;
  padding: 12px;
  overflow-x: auto;
  margin: 8px 0;
}

.markdown-content :deep(code.hljs) {
  background: transparent;
  padding: 0;
}

.markdown-content :deep(.inline-code) {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin: 8px 0;
  padding-left: 24px;
}

.markdown-content :deep(li) {
  margin: 4px 0;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3) {
  margin-top: 16px;
  margin-bottom: 8px;
  font-weight: 600;
}

.markdown-content :deep(h1) {
  font-size: 1.5em;
}

.markdown-content :deep(h2) {
  font-size: 1.3em;
}

.markdown-content :deep(h3) {
  font-size: 1.1em;
}

.markdown-content :deep(blockquote) {
  border-left: 3px solid #ddd;
  padding-left: 12px;
  margin: 8px 0;
  color: #666;
}

.markdown-content :deep(a) {
  color: #0066cc;
  text-decoration: underline;
}

/* KaTeX math styling */
.markdown-content :deep(.katex) {
  font-size: 1.1em;
}

.markdown-content :deep(.katex-display) {
  margin: 12px 0;
  overflow-x: auto;
  overflow-y: hidden;
}
</style>
